import { isSupportedLocale, type Locale } from './config';
import { i18n } from './i18n';
import type { LocaleStore } from './localeStore';
import { deviceLanguage, resolveLocale } from './resolveLocale';

type LocaleControllerOptions = {
  store: LocaleStore;
  /** The device's preferred languages, most preferred first. Read again on every decision. */
  deviceLocales: () => readonly string[];
};

/**
 * Every way the UI language can change, behind one function that decides it (MDC-4669).
 *
 * Two things move it: the user picking a language, and their settings arriving with one picked on
 * another device. Each of them only updates what is stored; the language shown is always decided the
 * same way from what is stored, by `resolve` — the chosen language if it is released, else the
 * device's, else English.
 *
 * Changing the language is `i18n.changeLanguage`, and everything that follows the language follows
 * that: dayjs through the instance's `languageChanged` listener, React through `useTranslation`.
 */
export type LocaleController = {
  /** The language the UI should be in now. What `initI18n` starts in. */
  resolve: () => Locale;
  /** Stores the user's choice — `null` forgets it — and switches to what follows. */
  choose: (locale: Locale | null) => Promise<void>;
  /**
   * Makes what is stored match the `language` the signed-in user's settings came back with.
   *
   * A language there is that user's choice, and is followed. `null` means the account has none, so a
   * choice still stored is another account's — the store outlives a sign-out, so the sign-in screen
   * stays in the language just used — and is forgotten, handing back to the device. A language the
   * apps carry no files for counts as none. `undefined` is settings not read yet, and changes nothing.
   */
  follow: (language: string | null | undefined) => Promise<void>;
  /**
   * The language to set a new user up in: the one chosen on this device — what onboarding was shown
   * in — else the device's own, released or not (see `deviceLanguage`). `null` for neither.
   */
  setupLanguage: () => Locale | null;
};

export const createLocaleController = ({ store, deviceLocales }: LocaleControllerOptions): LocaleController => {
  const resolve = () => resolveLocale({ stored: store.read(), deviceLocales: deviceLocales() });

  const choose = async (locale: Locale | null) => {
    store.write(locale);

    const next = resolve();

    if (i18n.language !== next) {
      await i18n.changeLanguage(next);
    }
  };

  return {
    resolve,
    choose,
    follow: async (language) => {
      if (language === undefined) {
        return;
      }

      const chosen = isSupportedLocale(language) ? language : null;

      if (chosen !== store.read()) {
        await choose(chosen);
      }
    },
    setupLanguage: () => store.read() ?? deviceLanguage(deviceLocales()),
  };
};
